/**
 * Google Tag Manager Provider for Analytics Manager
 * Handles all GTM/GA4 tracking through Analytics Manager
 */

export default class GTMProvider {
  constructor() {
    this.name = 'GTM';
    this.initialized = false;
    this.containerId = null;
    this.propertyContext = null;
    this.consentState = {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      functionality_storage: 'granted',
      security_storage: 'granted'
    };

    console.log('[GTM Provider] Created');
  }

  /**
   * Initialize GTM with configuration
   */
  initialize(config, propertyContext) {
    this.propertyContext = propertyContext;

    // Get container ID from config
    this.containerId = config.container_id || config.gtm_id;

    if (!this.containerId) {
      console.error('[GTM Provider] No container ID provided');
      return false;
    }

    // Initialize dataLayer if not exists
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
    }

    // Load GTM script
    this.loadGTMScript();

    // Initialize consent (GDPR compliant - denied by default)
    this.initializeConsent();

    // Set up property tracking
    this.initializePropertyTracking();

    this.initialized = true;
    console.log('[GTM Provider] Initialized with container:', this.containerId);
    return true;
  }

  /**
   * Load GTM script dynamically
   */
  loadGTMScript() {
    if (typeof window === 'undefined') return;

    // Check if GTM already loaded
    if (window.google_tag_manager) {
      console.log('[GTM Provider] GTM already loaded');
      return;
    }

    // GTM script injection
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${this.containerId}`;

    // GTM initialization code
    const initScript = document.createElement('script');
    initScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${this.containerId}', {
        send_page_view: false // We'll handle page views through Analytics Manager
      });
    `;

    document.head.appendChild(initScript);
    document.head.appendChild(script);

    // Add GTM noscript fallback
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${this.containerId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    // Expose gtag globally
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
  }

  /**
   * Initialize consent with GDPR defaults
   */
  initializeConsent() {
    if (typeof window === 'undefined') return;

    // Set default consent (GDPR compliant)
    window.dataLayer.push({
      event: 'gtm_consent_init',
      gtm: {
        start: new Date().getTime(),
        consent: this.consentState
      }
    });

    // Check for existing consent
    if (window.analyticsConsent) {
      this.updateConsent(window.analyticsConsent);
    }

    console.log('[GTM Provider] Consent initialized with defaults');
  }

  /**
   * Initialize property-specific tracking
   */
  initializePropertyTracking() {
    if (!this.propertyContext) return;

    const trackingConfig = {
      event: 'gtm_config_loaded',
      property_config: {
        property_id: this.propertyContext.property_id,
        property_name: this.propertyContext.property_name,
        property_brand: this.propertyContext.property_brand,
        property_type: this.propertyContext.property_type,
        location: this.propertyContext.location
      }
    };

    this.pushToDataLayer(trackingConfig);
    console.log('[GTM Provider] Property tracking configured');
  }

  /**
   * Update consent state
   */
  updateConsent(consent) {
    if (typeof window === 'undefined') return;

    // Map Analytics Manager consent to GTM consent
    this.consentState = {
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      ad_storage: consent.marketing ? 'granted' : 'denied',
      ad_user_data: consent.marketing ? 'granted' : 'denied',
      ad_personalization: consent.marketing ? 'granted' : 'denied',
      functionality_storage: consent.experience ? 'granted' : 'denied',
      security_storage: 'granted' // Always granted for technical cookies
    };

    // Update GTM consent
    window.dataLayer.push({
      event: 'gtm_consent_update',
      gtm: {
        consent: this.consentState
      }
    });

    // Send consent update event for backward compatibility
    window.dataLayer.push({
      event: 'consent_update',
      consent_state: this.consentState,
      consent_preferences: consent
    });

    console.log('[GTM Provider] Consent updated:', this.consentState);
  }

  /**
   * Track analytics event (main Analytics Manager interface)
   */
  track(eventName, eventData) {
    if (!this.initialized) {
      console.warn('[GTM Provider] Not initialized, skipping event:', eventName);
      return;
    }

    // Transform event for GTM format
    const gtmEvent = this.transformEvent(eventName, eventData);

    // Push to dataLayer
    this.pushToDataLayer(gtmEvent);

    console.log(`[GTM Provider] Event tracked: ${eventName}`);
  }

  /**
   * Transform Analytics Manager event to GTM format
   */
  transformEvent(eventName, eventData) {
    const baseEvent = {
      event: eventName,
      timestamp: eventData.timestamp || Date.now(),
      ...this.addPropertyContext(eventData)
    };

    // Handle specific event types
    switch (eventName) {
      case 'page_view':
        return {
          ...baseEvent,
          page_title: eventData.page_title,
          page_location: eventData.page_location || eventData.page_url,
          page_path: eventData.page_path,
          user_id: eventData.user_id
        };

      case 'add_to_cart':
      case 'remove_from_cart':
      case 'view_cart':
      case 'begin_checkout':
      case 'purchase':
        return {
          ...baseEvent,
          ecommerce: {
            currency: eventData.currency || 'EUR',
            value: eventData.value || 0,
            transaction_id: eventData.transaction_id,
            tax: eventData.tax,
            shipping: eventData.shipping,
            coupon: eventData.coupon,
            items: this.transformItems(eventData.items || [])
          }
        };

      case 'view_item':
      case 'view_item_list':
        return {
          ...baseEvent,
          ecommerce: {
            currency: eventData.currency || 'EUR',
            value: eventData.value || 0,
            items: this.transformItems(eventData.items || [])
          }
        };

      case 'search':
        return {
          ...baseEvent,
          search_term: eventData.search_term,
          number_of_rooms: eventData.number_of_rooms,
          number_of_nights: eventData.number_of_nights,
          number_of_guests: eventData.number_of_guests,
          location: eventData.location
        };

      case 'view_search_results':
        return {
          ...baseEvent,
          search_term: eventData.search_term,
          number_of_results: eventData.number_of_results
        };

      case 'select_content':
        return {
          ...baseEvent,
          content_type: eventData.content_type,
          item_id: eventData.item_id
        };

      case 'user_engagement':
        return {
          ...baseEvent,
          engagement_time_msec: eventData.engagement_time_msec
        };

      case 'scroll':
        return {
          ...baseEvent,
          percent_scrolled: eventData.percent_scrolled
        };

      case 'form_start':
      case 'form_submit':
        return {
          ...baseEvent,
          form_id: eventData.form_id,
          form_name: eventData.form_name
        };

      default:
        // Generic event - pass through all data
        return baseEvent;
    }
  }

  /**
   * Transform items to GA4 schema
   */
  transformItems(items) {
    return items.map((item, index) => ({
      item_id: String(item.item_id || item.id),
      item_name: item.item_name || item.name || item.room_type_name || '',
      affiliation: item.affiliation || this.propertyContext?.property_name || 'Direct Booking',
      coupon: item.coupon || item.promo_code,
      currency: item.currency || 'EUR',
      discount: item.discount || item.discount_amount || 0,
      index: item.index || index,
      item_brand: item.item_brand || this.propertyContext?.property_brand || '',
      item_category: item.item_category || item.category || 'Accommodation',
      item_category2: item.item_category2 || item.room_category || item.room_type,
      item_category3: item.item_category3 || item.rate_plan || item.board_type,
      item_list_id: item.item_list_id || 'room_selection',
      item_list_name: item.item_list_name || 'Available Rooms',
      item_variant: item.item_variant || item.occupancy || `${item.guests || 2} guests`,
      location_id: item.location_id || this.propertyContext?.property_id,
      price: item.price || 0,
      quantity: item.quantity || 1,
      // Hotel-specific fields
      start_date: item.start_date,
      end_date: item.end_date,
      nights: item.nights,
      guests: item.guests
    }));
  }

  /**
   * Add property context to event
   */
  addPropertyContext(eventData) {
    if (!this.propertyContext) return eventData;

    return {
      ...eventData,
      property_context: {
        property_id: this.propertyContext.property_id,
        property_name: this.propertyContext.property_name,
        property_brand: this.propertyContext.property_brand,
        property_type: this.propertyContext.property_type,
        location: this.propertyContext.location
      }
    };
  }

  /**
   * Push event to dataLayer with error handling
   */
  pushToDataLayer(event) {
    if (typeof window === 'undefined' || !window.dataLayer) {
      console.warn('[GTM Provider] DataLayer not available');
      return;
    }

    try {
      // Clear ecommerce object before pushing new e-commerce event
      if (event.ecommerce) {
        window.dataLayer.push({ ecommerce: null });
      }

      window.dataLayer.push(event);
    } catch (error) {
      console.error('[GTM Provider] Failed to push event:', error);
    }
  }

  /**
   * Get provider status for debugging
   */
  getStatus() {
    return {
      name: this.name,
      initialized: this.initialized,
      containerId: this.containerId,
      hasDataLayer: typeof window !== 'undefined' && !!window.dataLayer,
      hasGTM: typeof window !== 'undefined' && !!window.google_tag_manager,
      consentState: this.consentState
    };
  }

  /**
   * Manual gtag interface (for backward compatibility)
   */
  gtag(...args) {
    if (typeof window !== 'undefined' && window.gtag) {
      return window.gtag(...args);
    } else {
      console.warn('[GTM Provider] gtag function not available');
    }
  }
}